import React, {useEffect, useState} from 'react';
import { useCourt } from "../context/contextHooks";
import { useStyles } from '../styles/styles';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from "@mui/material/Grid";
import {getCurrency, statesLong} from "../global/utils";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormLabel from '@mui/material/FormLabel';
import { PatternFormat } from "react-number-format";
import Link from '@mui/material/Link';
import Popup from '../layout/Popup';
import ShortTandC from "../layout/ShortTandC";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";

const CheckData = (props) => {
    const paymentRequest = props.paymentRequest;
    const paymentFields = props.paymentFields;
    const setPaymentFields = props.setPaymentFields;
    const setAcceptTerms = props.setAcceptTerms;
    const splitFees = props.splitFees;
    const [court,] = useCourt();
    // aria-invalid used to be hasValidationErrors() on every control at once, so a
    // single bad field marked all of them invalid and nothing told a screen reader
    // which one was actually at fault -- the exact complaint in CPDEV-2777. These
    // read the reported errors, so only the offending control is marked.
    //
    // The attribute also has to sit on the input itself. As a JSX prop on TextField
    // it lands on the wrapper div, where it means nothing, which is where it was.
    const errors = props.fieldErrors || [];
    const isInvalid = (field) => (errors.some((e) => e.field === field) ? 'true' : 'false');
    // Only the control focus lands on describes itself with the summary Alert, so
    // the whole message is read on arrival. Returning undefined elsewhere leaves no
    // dangling IDREF -- React omits an attribute whose value is undefined.
    const firstErrorField = errors.find((e) => e.field)?.field;
    const describedBy = (field) => (field === firstErrorField ? 'payment-error' : undefined);

    const classes = useStyles;
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [openTandC, setOpenTandC] = useState(false);
    const [name, setName] = useState(paymentRequest.payorName);

    // The guard here used to be `if (val && ...)`, which meant clearing the field
    // left the previous value in paymentFields -- so a blanked field submitted the
    // value the user thought they had removed, and no required check could see it
    // (CPDEV-2777). An unchanged value is still skipped; an emptied one is not.
    const handleName = (e) => {
        const val = e.target.value;
        if (val !== paymentFields.payorName) {
            setPaymentFields({...paymentFields, payorName: val});
        }
    }

    // These two only recorded a complete-looking value, so clearing or shortening
    // either one kept the old number: validation passed on it and the payment went
    // through with the number the user had erased. Record what is in the box and
    // let validateCheckFields judge it -- the same fix handleName got.
    const handleRoutingNumber = (values) => {
        setPaymentFields({...paymentFields, routingNumber: values?.value ?? ''});
    }

    const handleAccountNumber = (e) => {
        setPaymentFields({...paymentFields, bankAccountNumber: e.target.value});
    }

    // Record whatever is in the box, partial or empty. Only taking a complete
    // value left the previous one in place, so an erased or shortened field went
    // on submitting what the user thought they had removed; the length check
    // belongs to validation, which can name the field.
    const handleZip = (values) => {
        const val = values?.value ?? '';
        if (val !== paymentFields.billingZip) {
            setPaymentFields({...paymentFields, billingZip: val});
        }
        if (val.length === 5) paymentRequest.billingZip = val;
    }

    const handleEmail = (e) => {
        const val = e.target.value;
        setPaymentFields({...paymentFields, email: val});
    }

    // A partial number used to leave the last complete one in place, so it was
    // submitted instead. It is recorded as typed now, and validation rejects it.
    const handlePhone = (values) => {
        if (values?.value.length === 10) {
            setPaymentFields({ ...paymentFields, phone: values.formattedValue });
        } else {
            setPaymentFields({ ...paymentFields, phone: values?.value || null });
        }
    };

    const closeTandC = (close) => {
        if (close) {
            setOpenTandC(false);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenTandC(true);
        }
    }


    useEffect(() => {
        setName(paymentRequest.payorName);
    }, [paymentRequest.payorName]);

    return (
        <>
            { openTandC &&
                <Popup open={openTandC} setDone={closeTandC} title="Terms and Conditions" component={<ShortTandC/>} />
            }
            <Paper variant='body1' elevation={3} sx={smallScreen ? {} : classes.controlPanel}>
                <Grid container spacing={1}>
                    <Grid
                        sx={{paddingLeft: '0', paddingTop: smallScreen ? '0' : '1rem'}}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        { splitFees ?
                            <Grid container spacing={1}>
                                <Grid style={{padding: '0'}} size={6}>
                                    <Typography color='primary' sx={{fontSize: '.9rem', textAlign: 'right'}}>Item Subtotal:</Typography>
                                </Grid>
                                <Grid style={{padding: '0'}} size={6}>
                                    <Typography color='textPrimary' sx={{fontSize: '.9rem', textAlign: 'right', paddingRight: '2rem'}}>
                                        {getCurrency(paymentRequest.courtAmount)}
                                    </Typography>
                                </Grid>
                                <Grid style={{padding: '0'}} size={6}>
                                    <Typography color='primary' sx={{fontSize: '.9rem', textAlign: 'right'}}>Processing Fee:</Typography>
                                </Grid>
                                <Grid style={{padding: '0'}} size={6}>
                                    <Typography color='textPrimary' sx={{fontSize: '.9rem', textAlign: 'right', paddingRight: '2rem'}}>
                                        {getCurrency(paymentRequest.feeAmount)}
                                    </Typography>
                                </Grid>
                                <Grid style={{padding: '0'}} size={6}>
                                    <Typography color='primary' sx={{fontSize: '.9rem', textAlign: 'right'}}>Payment Total:</Typography>
                                </Grid>
                                <Grid style={{padding: '0'}} size={6}>
                                    <Typography color='textPrimary' sx={{fontSize: '.9rem', textAlign: 'right', paddingRight: '2rem'}}>
                                        {getCurrency(paymentRequest.totalAmount)}
                                    </Typography>
                                </Grid>
                            </Grid>:
                            <>
                                <Typography component='span' variant='subtitle1' color='primary' sx={{fontSize: '1.2rem'}}>
                                    Amount:
                                </Typography>
                                <Typography component='span' variant='subtitle2' color='textPrimary' sx={{fontSize: '1.2rem', marginLeft: '1rem'}}>
                                    {getCurrency(paymentRequest.totalAmount)}
                                </Typography>
                            </>
                        }
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='name' required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Name on Account
                            </FormLabel>
                            <TextField
                                id='name'
                                name='name'
                                variant='outlined'
                                fullWidth
                                defaultValue={paymentRequest.payorName}
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    handleName(e);
                                }}
                                slotProps={{
                                    htmlInput: {
                                        sx: classes.textbox,
                                        "aria-required": "true",
                                        "aria-invalid": isInvalid('name'),
                                        "aria-describedby": describedBy('name'),
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        sx={{paddingLeft: '0'}}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <FormControl fullWidth sx={{marginLeft: '0'}}>
                            <FormLabel htmlFor='routingNumber' required sx={classes.inputLabel}>
                                Routing Number</FormLabel>
                            <PatternFormat
                                id='routingNumber'
                                name='routingNumber'
                                customInput={TextField}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                format='#########'
                                onValueChange={(values) => handleRoutingNumber(values)}
                                slotProps={{
                                    input: {
                                        sx: classes.numberbox,
                                        inputProps: {"aria-required": "true", "aria-invalid": isInvalid('routingNumber'), "aria-describedby": describedBy('routingNumber')}
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        sx={{paddingLeft: '0'}}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='accountNumber' required sx={classes.inputLabel}>Bank Account Number</FormLabel>
                            <TextField
                                id='accountNumber'
                                name='accountNumber'
                                variant='outlined'
                                fullWidth
                                onBlur={handleAccountNumber}
                                slotProps={{
                                    htmlInput: {
                                        sx: classes.textbox,
                                        "aria-required": "true",
                                        "aria-invalid": isInvalid('accountNumber'),
                                        "aria-describedby": describedBy('accountNumber'),
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                    { court?.requireAddress &&
                        <>
                            <Grid
                                sx={{paddingLeft: '0'}}
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <FormControl fullWidth >
                                    <FormLabel htmlFor='address' required={!!court?.requireAddress} sx={classes.inputLabel}>Billing Address</FormLabel>
                                    <TextField
                                        id='address'
                                        name='address'
                                        variant='outlined'
                                        fullWidth
                                        defaultValue={paymentRequest.payorAddress}
                                        onChange={(e) => paymentRequest.payorAddress = e.target.value}
                                        slotProps={{
                                            htmlInput: {
                                                sx: classes.textbox,
                                                "aria-required": !!court?.requireAddress ? "true" : "false",
                                                "aria-invalid": isInvalid('address'),
                                                "aria-describedby": describedBy('address'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                                    <FormLabel htmlFor='city' required={!!court?.requireAddress} sx={classes.inputLabel}>Billing City</FormLabel>
                                    <TextField
                                        id='city'
                                        name='city'
                                        variant='outlined'
                                        fullWidth
                                        defaultValue={paymentRequest?.payorCity}
                                        onChange={(e) => paymentRequest.payorCity = e.target.value}
                                        slotProps={{
                                            htmlInput: {
                                                sx: classes.textbox,
                                                "aria-required": "true",
                                                "aria-invalid": isInvalid('city'),
                                                "aria-describedby": describedBy('city'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                sx={{paddingLeft: '0'}}
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor='state' required={!!court?.requireAddress} sx={classes.inputLabel}>Billing State</FormLabel>
                                    <Select
                                        id='state'
                                        name='state'
                                        native
                                        variant='outlined'
                                        fullWidth
                                        defaultValue={paymentRequest?.payorState}
                                        onChange={(e) => paymentRequest.payorState = e.target.value}
                                        sx={{height: '2rem'}}
                                        inputProps={{
                                            id: 'optionSelect',
                                            "aria-required": "true",
                                            "aria-invalid": isInvalid('state'),
                                            "aria-describedby": describedBy('state'),
                                        }}
                                    >
                                        <option key='None' value=''>Select your state</option>
                                        {statesLong.map((state) => (
                                            <option key={state.key} value={state.value} style={{fontSize: '.9rem'}}>{state.text}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid
                                sx={{paddingLeft: smallScreen ? '0' : '.5rem'}}
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                                    <FormLabel htmlFor='billingZip' required={!!court.requireZip} sx={classes.inputLabel}>Billing Zip</FormLabel>
                                    <PatternFormat
                                        id='billingZip'
                                        name='billingZip'
                                        customInput={TextField}
                                        type='text'
                                        autoComplete='off'
                                        variant='outlined'
                                        format='#####'
                                        defaultValue={paymentRequest.billingZip}
                                        onValueChange={(values) => handleZip(values)}
                                        slotProps={{
                                            input: {
                                                sx: classes.numberbox,
                                                inputProps: {
                                                    "aria-required": !!court.requireZip ? "true" : "false",
                                                    "aria-invalid": isInvalid('billingZip'),
                                                    "aria-describedby": describedBy('billingZip'),
                                                }
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                        </>
                    }
                    <Grid
                        sx={{paddingLeft: '0'}}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <FormControl fullWidth>
                            <FormLabel htmlFor='email' sx={classes.inputLabel}>Email Address</FormLabel>
                            <TextField
                                id='email'
                                name='email'
                                variant='outlined'
                                fullWidth
                                defaultValue={paymentRequest.email}
                                onBlur={handleEmail}
                                autoComplete='email'
                                slotProps={{
                                    htmlInput: {sx: classes.textbox, "aria-invalid": isInvalid('email'), "aria-describedby": describedBy('email')}
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        sx={{paddingLeft: '0'}}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <FormControl fullWidth sx={{marginLeft: smallScreen ? '0' : '.5rem'}}>
                            <FormLabel htmlFor='phone' sx={classes.inputLabel}>Phone Number</FormLabel>
                            <PatternFormat
                                id='phone'
                                name='phone'
                                customInput={TextField}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                format="(###) ###-####"
                                defaultValue={paymentRequest.phone}
                                onValueChange={(values) => handlePhone(values)}
                                slotProps={{
                                    input: {sx: classes.numberbox, inputProps: {"aria-invalid": isInvalid('phone'), "aria-describedby": describedBy('phone')}}
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid sx={{paddingLeft: '0'}} size={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="tandc"
                                    color='primary'
                                    defaultChecked={false}
                                    onChange={(e) => {setAcceptTerms(e.target.checked)}}
                                />
                            }
                            label={ smallScreen ?
                                <Typography component="span" sx={{fontSize: '.9rem'}}>
                                    I accept the <Link onClick={() => {setOpenTandC(true)}} onKeyDown={handleKeyDown} href="#" aria-haspopup="dialog">Payment Terms</Link>
                                </Typography> :
                                <Typography component="span" sx={{fontSize: '1.2rem'}}>
                                    I agree to the <Link onClick={() => {setOpenTandC(true)}} onKeyDown={handleKeyDown} href="#" aria-haspopup="dialog">CitePayUSA Terms and Conditions</Link>
                                </Typography>
                            }
                            sx={{...classes.checkbox, marginTop: '1rem', marginLeft: '-2rem', paddingLeft: '0', lineHeight: smallScreen ? '.85rem' : ''}}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default CheckData;
